/**
 * Commands the palette offers on every page: New item and New mission (which open the
 * Missions page's own dialogs), Answer the next request (in the answer sheet, ADR 0072) and
 * Review what needs you when some wait, and the phone layout.
 */
import { Inbox, Plus, Rocket, ShieldQuestion, Smartphone } from '@orgus/ui/icons';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { mobileUrl } from '@orgus/protocol';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { useWaitingOnYou } from '../approvals/waiting.ts';
import { openAnswer } from '../approvals/AnswerSheet.tsx';
import { openNewItem, openNewMission } from './launch.ts';

/** A global command is hidden while the page that owns the real one is mounted. */
export const SHADOWED_BY: Record<string, string> = {
  'palette.item.new': 'missions.item.new',
  'palette.mission.new': 'missions.new',
};

/** Page commands the palette lists itself, from all of the data, so the page's copies are skipped. */
export const PALETTE_OWNS = ['missions.item:', 'missions.open:'];

export function usePaletteCommands(): void {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // The one selector (ADR 0056): what the Inbox lists, blocked first.
  const w = useWaitingOnYou();
  const pending = w.total;
  // The one order (ADR 0072): the first pending request is the most urgent.
  const next = w.approvals[0] ?? null;
  const pendingSub = [w.urgent && `${w.urgent} ${w.urgent === 1 ? 'needs' : 'need'} you`, w.asked.length && `${w.asked.length} asked`].filter(Boolean).join(', ');
  const cmds = useMemo<Command[]>(() => {
    const list: Command[] = [
      {
        id: 'palette.item.new',
        title: 'New item',
        subtitle: 'An epic, story, task or defect in the backlog',
        group: 'Actions',
        icon: Plus,
        shortcut: ['c'],
        keywords: ['create', 'add', 'epic', 'story', 'task', 'defect', 'bug', 'backlog', 'requirement', 'feature'],
        order: 5,
        perform: () => openNewItem(navigate),
      },
      {
        id: 'palette.mission.new',
        title: 'New mission',
        subtitle: 'Give Claude Code a goal on one of your machines',
        group: 'Actions',
        icon: Rocket,
        keywords: ['start', 'create', 'goal', 'agent'],
        order: 6,
        perform: () => openNewMission(navigate),
      },
      {
        id: 'palette.phone',
        title: 'Use the phone layout',
        subtitle: 'Inbox, missions and fleet under your thumb',
        group: 'General',
        icon: Smartphone,
        keywords: ['mobile', 'phone', 'pwa', 'small screen'],
        perform: () => navigate(mobileUrl(`${pathname}${search}`)),
      },
    ];
    // Answer where you are (ADR 0072): the most urgent request opens in the answer sheet, here.
    if (next) {
      list.push({
        id: 'palette.approvals.next',
        title: 'Answer the next request',
        subtitle: next.title,
        group: 'Actions',
        icon: ShieldQuestion,
        keywords: ['approve', 'allow', 'deny', 'needs you', 'hitl', 'permission', 'request'],
        order: 0,
        perform: () => openAnswer(next.id),
      });
    }
    if (pending > 0) {
      list.push({
        id: 'palette.approvals',
        title: 'Review what needs you',
        subtitle: pendingSub,
        group: 'Actions',
        icon: Inbox,
        keywords: ['approve', 'deny', 'needs you', 'hitl', 'waiting', 'permission'],
        order: 0,
        perform: () => navigate('/inbox'),
      });
    }
    return list;
  }, [navigate, pathname, search, pending, pendingSub, next]);
  useRegisterCommands(cmds, [cmds]);
}
